import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { invitations } from '../../services/api';

const DIFFICULTY_LABELS = {
  beginner: 'Principiant',
  intermediate: 'Intermedi',
  advanced: 'Avançat'
};

function JoinWithCode({ onClose, onSuccess }) {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [verifiedData, setVerifiedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;

    try {
      setLoading(true);
      setError('');
      setVerifiedData(null);

      const { data } = await invitations.verify(code.trim().toUpperCase());
      setVerifiedData(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Codi d\'invitació no vàlid');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    try {
      setJoining(true);
      setError('');

      const { data } = await invitations.join(code.trim().toUpperCase());

      if (onSuccess) {
        onSuccess(data);
      }

      // If project is not active, don't navigate - show success message instead
      if (data.projectNotActive) {
        setVerifiedData(null);
        setCode('');
        setError('');
        // Show success with warning
        alert(data.message + '\n\n' + data.warning);
        if (onClose) {
          onClose();
        }
        return;
      }

      // Navigate to the case
      if (data.caseId) {
        navigate(`/cases/${data.caseId}`);
      }

      if (onClose) {
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'No s\'ha pogut unir');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Unir-se amb codi</h2>
            {onClose && (
              <button onClick={onClose} className="text-content-muted hover:text-content-secondary">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {error && (
            <div className="mb-4 bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {!verifiedData ? (
            // Step 1: Enter and verify code
            <form onSubmit={handleVerify}>
              <p className="text-content-secondary mb-4">
                Introdueix el codi d'invitació que t'ha proporcionat l'instructor per unir-te a un cas o equip.
              </p>
              <input
                type="text"
                placeholder="Introdueix el codi (ex: ABCD1234)"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="input w-full text-center text-2xl font-mono tracking-wider"
                autoFocus
                maxLength={20}
              />
              <button
                type="submit"
                disabled={loading || !code.trim()}
                className="btn btn-primary w-full mt-4 disabled:opacity-50"
              >
                {loading ? 'Verificant...' : 'Verificar codi'}
              </button>
            </form>
          ) : (
            // Step 2: Confirm and join
            <div>
              <div className="bg-success-subtle border border-success/30 rounded-lg p-4 mb-4">
                <div className="flex items-center text-success-text mb-2">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">Codi vàlid</span>
                </div>
                <p className="text-success-text text-sm">
                  Aquest codi t'inscriurà a:
                </p>
              </div>

              {verifiedData.type === 'case' && verifiedData.case && (
                <div className="border rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-lg">{verifiedData.case.title}</h3>
                  {verifiedData.case.description && (
                    <p className="text-content-secondary text-sm mt-1 line-clamp-2">
                      {verifiedData.case.description}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {verifiedData.case.difficulty && (
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        verifiedData.case.difficulty === 'beginner' ? 'bg-success-subtle text-success-text' :
                        verifiedData.case.difficulty === 'intermediate' ? 'bg-warning-subtle text-warning-text' :
                        'bg-error-subtle text-error-text'
                      }`}>
                        {DIFFICULTY_LABELS[verifiedData.case.difficulty] || verifiedData.case.difficulty}
                      </span>
                    )}
                    {verifiedData.case.timeLimitMinutes && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-info-subtle text-info-text">
                        {Math.floor(verifiedData.case.timeLimitMinutes / 60)}h {verifiedData.case.timeLimitMinutes % 60}min
                      </span>
                    )}
                  </div>
                  {verifiedData.case.projectTitle && (
                    <p className="text-sm text-content-muted mt-2">
                      Projecte: {verifiedData.case.projectTitle}
                    </p>
                  )}
                </div>
              )}

              {/* Warning for inactive project */}
              {verifiedData.warning && (
                <div className="bg-warning-subtle border border-warning/30 rounded-lg p-3 mb-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-sm text-warning-text">{verifiedData.warning}</p>
                  </div>
                </div>
              )}

              {verifiedData.type === 'team' && verifiedData.team && (
                <div className="border rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-lg">{verifiedData.team.name}</h3>
                  {verifiedData.team.caseTitle && (
                    <p className="text-content-secondary text-sm mt-1">
                      Per al cas: {verifiedData.team.caseTitle}
                    </p>
                  )}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setVerifiedData(null);
                    setCode('');
                    setError('');
                  }}
                  className="btn btn-secondary flex-1"
                >
                  Cancel·lar
                </button>
                <button
                  onClick={handleJoin}
                  disabled={joining}
                  className="btn btn-primary flex-1 disabled:opacity-50"
                >
                  {joining ? 'Unint-se...' : 'Confirmar i unir-se'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default JoinWithCode;
